import {
  Alert as AlertType,
  AlertTypes,
  assertNever,
  cn,
  getPartnerEventName,
  getTimeAgoInWords,
  parseDate,
  PartnerEvents,
  pluralize,
} from "@theprelude/core";
import {
  AlertIcon,
  CheckmarkIcon,
  LightningIcon,
  ScanIcon,
  Truncate,
  UserIcon,
} from "@theprelude/ds";
import { forwardRef, HTMLAttributes } from "react";

interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  alert: AlertType;
}

export const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  { alert, className, ...props },
  ref,
) {
  const icon = getAlertIcon(alert);
  const message = getAlertMessage(alert);

  return (
    <div
      ref={ref}
      className={cn(
        "flex items-center gap-2 p-2",
        alert.read_at
          ? "bg-surface-background"
          : "cursor-pointer bg-notification/5",
        className,
      )}
      {...props}
    >
      <div className="flex size-8 items-center justify-center rounded-full bg-surface-background">
        {icon}
      </div>
      <div className="flex flex-1 flex-col overflow-hidden">
        <Truncate text={message}>{message}</Truncate>
        <div className="text-xs text-secondary-foreground">
          {getTimeAgoInWords(parseDate(alert.created_at))}
        </div>
      </div>
      {!alert.read_at && (
        <div className="h-2 w-2 rounded-full bg-notification" />
      )}
    </div>
  );
});

export const getAlertMessage = (alert: AlertType): string => {
  try {
    switch (alert.alert_type) {
      case AlertTypes.NEW_MISSING_AV_POLICY_ENDPOINTS: {
        const count = alert.details.count;
        return `${count.toLocaleString()} more ${pluralize(count, "endpoint")} ${pluralize(count, "has", "have")} joined ${getPartnerEventName(PartnerEvents.MISSING_AV_POLICY)}.`;
      }
      case AlertTypes.NEW_MISSING_EDR_POLICY_ENDPOINTS: {
        const count = alert.details.count;
        return `${count.toLocaleString()} more ${pluralize(count, "endpoint")} ${pluralize(count, "has", "have")} joined ${getPartnerEventName(PartnerEvents.MISSING_EDR_POLICY)}.`;
      }
      case AlertTypes.NEW_MISSING_SCAN_ENDPOINTS: {
        const count = alert.details.count;
        return `${count.toLocaleString()} new ${pluralize(count, "endpoint")} ${pluralize(count, "has", "have")} joined ${getPartnerEventName(PartnerEvents.MISSING_SCAN)}.`;
      }
      case AlertTypes.NEW_OUT_OF_DATE_SCAN_ENDPOINTS: {
        const count = alert.details.count;
        return `${count.toLocaleString()} new ${pluralize(count, "endpoint")} ${pluralize(count, "has", "have")} joined ${getPartnerEventName(PartnerEvents.OUT_OF_DATE_SCAN)}.`;
      }
      case AlertTypes.NEW_MISSING_MFA_USERS: {
        const count = alert.details.count;
        return `${count.toLocaleString()} more ${pluralize(count, "user")} ${pluralize(count, "has", "have")} joined ${getPartnerEventName(PartnerEvents.MISSING_MFA)}.`;
      }
      case AlertTypes.NEW_USER_MISSING_ASSET_MANAGER: {
        const count = alert.details.count;
        return `${count.toLocaleString()} more ${pluralize(count, "user")} ${pluralize(count, "has", "have")} joined ${getPartnerEventName(PartnerEvents.USER_MISSING_ASSET_MANAGER)}.`;
      }
      case AlertTypes.NEW_USER_MISSING_EDR: {
        const count = alert.details.count;
        return `${count.toLocaleString()} more ${pluralize(count, "user")} ${pluralize(count, "has", "have")} joined ${getPartnerEventName(PartnerEvents.USER_MISSING_EDR)}.`;
      }
      case AlertTypes.NEW_USER_MISSING_VULN_MANAGER: {
        const count = alert.details.count;
        return `${count.toLocaleString()} more ${pluralize(count, "user")} ${pluralize(count, "has", "have")} joined ${getPartnerEventName(PartnerEvents.USER_MISSING_VULN_MANAGER)}.`;
      }
      case AlertTypes.NEW_NO_ASSET_MANAGER_ENDPOINTS: {
        const count = alert.details.count;
        return `${count.toLocaleString()} more ${pluralize(count, "endpoint")} ${pluralize(count, "has", "have")} joined ${getPartnerEventName(PartnerEvents.USER_MISSING_ASSET_MANAGER)}.`;
      }
      case AlertTypes.NEW_NO_EDR_ENDPOINTS: {
        const count = alert.details.count;
        return `${count.toLocaleString()} more ${pluralize(count, "endpoint")} ${pluralize(count, "has", "have")} joined ${getPartnerEventName(PartnerEvents.USER_MISSING_EDR)}.`;
      }
      case AlertTypes.NEW_NO_VULN_MANAGER_ENDPOINTS: {
        const count = alert.details.count;
        return `${count.toLocaleString()} more ${pluralize(count, "endpoint")} ${pluralize(count, "has", "have")} joined ${getPartnerEventName(PartnerEvents.NO_VULN_MANAGER)}.`;
      }
      case AlertTypes.NEW_REDUCED_FUNCTIONALITY_MODE_ENDPOINTS: {
        const count = alert.details.count;
        return `${count.toLocaleString()} more ${pluralize(count, "endpoint")} ${pluralize(count, "has", "have")} joined ${getPartnerEventName(PartnerEvents.REDUCED_FUNCTIONALITY_MODE)}.`;
      }
      case AlertTypes.NEW_POLICY_SETTING_FAILURE: {
        const count = alert.details.affected.length;
        return `${count.toLocaleString()} new ${pluralize(count, "policy", "policies")} ${pluralize(count, "has", "have")} failed.`;
      }
      case AlertTypes.NEW_POLICY_SETTING_PASS: {
        const count = alert.details.affected.length;
        return `${count.toLocaleString()} new ${pluralize(count, "policy", "policies")} ${pluralize(count, "has", "have")} passed.`;
      }
      default: {
        return assertNever(alert);
      }
    }
  } catch {
    return "Unknown";
  }
};

export const getAlertIcon = (alert: AlertType) => {
  try {
    switch (alert.alert_type) {
      case AlertTypes.NEW_MISSING_AV_POLICY_ENDPOINTS:
      case AlertTypes.NEW_MISSING_EDR_POLICY_ENDPOINTS:
      case AlertTypes.NEW_MISSING_MFA_USERS:
      case AlertTypes.NEW_NO_ASSET_MANAGER_ENDPOINTS:
      case AlertTypes.NEW_NO_EDR_ENDPOINTS:
      case AlertTypes.NEW_NO_VULN_MANAGER_ENDPOINTS:
      case AlertTypes.NEW_REDUCED_FUNCTIONALITY_MODE_ENDPOINTS: {
        return (
          <LightningIcon
            className={
              alert.read_at ? "text-secondary-foreground" : "text-status-yellow"
            }
          />
        );
      }
      case AlertTypes.NEW_POLICY_SETTING_FAILURE: {
        return (
          <AlertIcon
            className={
              alert.read_at ? "text-secondary-foreground" : "text-status-red"
            }
          />
        );
      }
      case AlertTypes.NEW_POLICY_SETTING_PASS: {
        return (
          <CheckmarkIcon
            className={
              alert.read_at ? "text-secondary-foreground" : "text-status-green"
            }
          />
        );
      }
      case AlertTypes.NEW_MISSING_SCAN_ENDPOINTS:
      case AlertTypes.NEW_OUT_OF_DATE_SCAN_ENDPOINTS: {
        return (
          <ScanIcon
            className={
              alert.read_at ? "text-secondary-foreground" : "text-status-red"
            }
          />
        );
      }
      case AlertTypes.NEW_USER_MISSING_ASSET_MANAGER:
      case AlertTypes.NEW_USER_MISSING_EDR:
      case AlertTypes.NEW_USER_MISSING_VULN_MANAGER: {
        return (
          <UserIcon
            className={
              alert.read_at ? "text-secondary-foreground" : "text-status-red"
            }
          />
        );
      }
      default: {
        return assertNever(alert);
      }
    }
  } catch {
    return (
      <AlertIcon
        className={
          alert.read_at
            ? "text-secondary-foreground"
            : "text-tertiary-foreground"
        }
      />
    );
  }
};
